import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
    Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, TextField, Typography,
} from '@mui/material';
import { MaintenanceService } from '../../services/maintenance.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PERMISSIONS } from '../../constants/permissions';
import { MeterReadingDTO, MeterSummary, mntLabel } from '../../interfaces/maintenance';
import { dhakaToday } from './UtilityLogSheet';

const cell = (basis: number) => ({ flex: `1 1 ${basis}px`, maxWidth: basis + 140, minWidth: 0 });

const daysAgo = (n: number): string => {
    const [y, m, d] = dhakaToday().split('-').map(Number);
    const t = new Date(Date.UTC(y, m - 1, d - n));
    return t.toISOString().slice(0, 10);
};

const W = 640;
const H = 220;
const PAD = 36;

/** Hand-drawn line chart (no chart library in the app). Points are oldest to newest. */
export const ReadingChart: React.FC<{ points: MeterReadingDTO[]; min?: number | null; max?: number | null; unit?: string | null }> = ({ points, min, max, unit }) => {
    if (points.length < 2) {
        return <Typography variant="body2" color="text.secondary">Not enough readings in this range to draw a chart.</Typography>;
    }
    const vals = points.map(p => p.value);
    const lo = Math.min(...vals, ...(min != null ? [min] : []));
    const hi = Math.max(...vals, ...(max != null ? [max] : []));
    const span = hi - lo || 1;
    const t0 = new Date(points[0].readAt).getTime();
    const t1 = new Date(points[points.length - 1].readAt).getTime();
    const tSpan = t1 - t0 || 1;
    const x = (p: MeterReadingDTO) => PAD + ((new Date(p.readAt).getTime() - t0) / tSpan) * (W - PAD * 2);
    const y = (v: number) => H - PAD - ((v - lo) / span) * (H - PAD * 2);
    const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
    return (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`Readings over time${unit ? ` in ${unit}` : ''}`}>
                {min != null && <line x1={PAD} x2={W - PAD} y1={y(min)} y2={y(min)} stroke="#d32f2f" strokeDasharray="4 4" />}
                {max != null && <line x1={PAD} x2={W - PAD} y1={y(max)} y2={y(max)} stroke="#d32f2f" strokeDasharray="4 4" />}
                <line x1={PAD} x2={PAD} y1={PAD} y2={H - PAD} stroke="#9e9e9e" />
                <line x1={PAD} x2={W - PAD} y1={H - PAD} y2={H - PAD} stroke="#9e9e9e" />
                <text x={4} y={PAD} fontSize="10" fill="#757575">{hi}</text>
                <text x={4} y={H - PAD} fontSize="10" fill="#757575">{lo}</text>
                <text x={PAD} y={H - 10} fontSize="10" fill="#757575">{points[0].readingDate}</text>
                <text x={W - PAD} y={H - 10} fontSize="10" fill="#757575" textAnchor="end">{points[points.length - 1].readingDate}</text>
                <path d={path} fill="none" stroke="#1976d2" strokeWidth="2" />
                {points.map(p => (
                    <circle key={p.id} cx={x(p)} cy={y(p.value)} r={p.outOfRange ? 4 : 2.5} fill={p.outOfRange ? '#d32f2f' : '#1976d2'} />
                ))}
            </svg>
        </Box>
    );
};

/** One meter's readings (maintenance L2): table, chart, and manager correction. */
const MeterReadingHistory: React.FC = () => {
    const { meterId = '' } = useParams();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canCorrect = hasPermission(PERMISSIONS.MAINTENANCE_WO_MANAGE);
    const [summary, setSummary] = useState<MeterSummary | null>(null);
    const [from, setFrom] = useState(daysAgo(30));
    const [to, setTo] = useState(dhakaToday());
    const [rows, setRows] = useState<MeterReadingDTO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [fix, setFix] = useState<MeterReadingDTO | null>(null);
    const [fixValue, setFixValue] = useState('');
    const [fixReset, setFixReset] = useState(false);
    const [fixNote, setFixNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        MaintenanceService.getMeterSummary(meterId).then(setSummary).catch(e => toast.showApiError(e, 'Failed to load the meter'));
    }, [meterId]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await MaintenanceService.listReadings(meterId, from, to, page, size);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load readings');
        } finally {
            setLoading(false);
        }
    }, [meterId, from, to, page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const chartPoints = useMemo(() => [...rows].reverse(), [rows]);
    const meter = summary?.meter;

    const openFix = (r: MeterReadingDTO) => {
        setFix(r);
        setFixValue(String(r.value));
        setFixReset(r.isReset);
        setFixNote('');
    };

    const submitFix = async () => {
        if (!fix) return;
        const v = Number(fixValue);
        if (fixValue.trim() === '' || !Number.isFinite(v)) { toast.showError('Enter a valid number'); return; }
        if (!fixNote.trim()) { toast.showError('A reason is required'); return; }
        try {
            setSaving(true);
            await MaintenanceService.correctReading(fix.id, v, fixReset, fixNote.trim());
            toast.showSuccess('Reading corrected');
            setFix(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to correct the reading');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }} data-help-page="maint.meterHistory.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography variant="h5">{meter ? `${summary!.equipmentCode} - ${meter.name}` : 'Meter readings'}</Typography>
                    {meter && (
                        <Typography variant="body2" color="text.secondary">
                            {summary!.equipmentName}{summary!.locationName ? `, ${summary!.locationName}` : ''} | {mntLabel(meter.kind)}
                            {meter.unit ? `, ${meter.unit}` : ''}
                        </Typography>
                    )}
                </Box>
                <Button data-help="maint.meterHistory.back" component={RouterLink} to="/maintenance/readings">Back to log sheet</Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="maint.meterHistory.filter">
                <TextField
                    size="small" type="date" label="From" data-help="maint.meterHistory.filter.from" value={from} sx={cell(160)} InputLabelProps={{ shrink: true }}
                    onChange={e => { setFrom(e.target.value); setPage(0); }}
                />
                <TextField
                    size="small" type="date" label="To" data-help="maint.meterHistory.filter.to" value={to} sx={cell(160)} InputLabelProps={{ shrink: true }}
                    onChange={e => { setTo(e.target.value); setPage(0); }}
                />
            </Box>

            <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.meterHistory.chart">
                <ReadingChart points={chartPoints} min={meter?.minValue} max={meter?.maxValue} unit={meter?.unit} />
            </Paper>

            <Paper data-help-section="maint.meterHistory.table">
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="maint.meterHistory.col.date">Date</TableCell>
                                    <TableCell data-help="maint.meterHistory.col.shift">Shift</TableCell>
                                    <TableCell data-help="maint.meterHistory.col.value" align="right">Value</TableCell>
                                    <TableCell data-help="maint.meterHistory.col.change" align="right">Change</TableCell>
                                    <TableCell data-help="maint.meterHistory.col.flags">Flags</TableCell>
                                    <TableCell data-help="maint.meterHistory.col.by">By</TableCell>
                                    <TableCell data-help="maint.meterHistory.col.note">Note</TableCell>
                                    {canCorrect && <TableCell data-help="maint.meterHistory.col.actions" />}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 && (
                                    <TableRow><TableCell colSpan={canCorrect ? 8 : 7} align="center">No readings in this range</TableCell></TableRow>
                                )}
                                {rows.map(r => (
                                    <TableRow key={r.id} hover>
                                        <TableCell>{r.readingDate}</TableCell>
                                        <TableCell>{r.shiftLabel ?? '-'}</TableCell>
                                        <TableCell align="right">{r.value}</TableCell>
                                        <TableCell align="right">{r.delta ?? '-'}</TableCell>
                                        <TableCell>
                                            {r.outOfRange && <Chip size="small" color="error" label="Out of range" sx={{ mr: 0.5 }} />}
                                            {r.isReset && <Chip size="small" color="warning" label="Reset" sx={{ mr: 0.5 }} />}
                                            {r.correctsReadingId && <Chip size="small" label="Corrected" sx={{ mr: 0.5 }} />}
                                            {r.source !== 'MANUAL' && <Chip size="small" variant="outlined" label={mntLabel(r.source)} />}
                                        </TableCell>
                                        <TableCell>{r.recordedByName ?? '-'}</TableCell>
                                        <TableCell>{r.note ?? ''}</TableCell>
                                        {canCorrect && (
                                            <TableCell><Button data-help="maint.meterHistory.correct" size="small" onClick={() => openFix(r)}>Correct</Button></TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <TablePagination
                    component="div" count={total} page={page} rowsPerPage={size} rowsPerPageOptions={[25, 50, 100]}
                    onPageChange={(_e, p) => setPage(p)}
                    onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
                />
            </Paper>

            <Dialog open={!!fix} onClose={() => setFix(null)} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'maint.readingFix.page' } as any}>
                <DialogTitle>Correct reading</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label="Correct value" type="number" data-help="maint.readingFix.field.value" value={fixValue} autoFocus
                        onChange={e => setFixValue(e.target.value)} inputProps={{ step: 'any' }}
                    />
                    {meter?.kind === 'CUMULATIVE' && (
                        <FormControlLabel data-help="maint.readingFix.field.reset"
                            label="Counter was reset or rolled over"
                            control={<Checkbox checked={fixReset} onChange={e => setFixReset(e.target.checked)} />}
                        />
                    )}
                    <TextField
                        label="Reason (required)" data-help="maint.readingFix.field.reason" value={fixNote} multiline minRows={2}
                        inputProps={{ maxLength: 500 }} onChange={e => setFixNote(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFix(null)} data-help="maint.readingFix.cancel">Cancel</Button>
                    <Button data-help="maint.meterHistory.action.saveFix" variant="contained" onClick={submitFix} disabled={saving}>Save correction</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MeterReadingHistory;
